import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { ConnectedPosition, Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    HostListener,
    inject,
    input,
    NgZone,
    OnDestroy,
    OnInit,
    output,
    signal,
    viewChild,
    ViewEncapsulation,
} from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatDialog, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import {
    blackLineRegex,
    blackLineSpan,
    findParentTag,
    ML2HtmlUtils,
    MOS_CONTENT_ID_PREFIX,
    PRODUCTION_COMMAND_ROW_SEPARATOR,
    PRODUCTION_CONTENT_ID_PREFIX,
    ProductionCommandsLangKey,
    ProductionCommandType,
    ProductionFieldName,
} from '@common/utils/ml-2-html-utils';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { environment } from '@src/environments/environment';
import { ModalResponseCodes, ModalService } from '@src/suite/common/components/modal/modal.service';
import { EnpsField } from '@src/suite/common/data-models/fields/enps-field';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { FROALA_DEFAULT_TOOLBAR_ITEMS, INITIAL_TIME_STAMP, KeyCodes, PRODUCTION_COMMAND_BUTTONS } from '@src/suite/common/enums/constants';
import { CodoxError, DRAGTYPES, EditorActions } from '@src/suite/common/enums/enums';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { MosEventService } from '@src/suite/common/services/mos-event.service';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { ProductionCommandDialogComponent } from '@src/suite/common/st-full-screen-dialog/production-command-dialog/production-command-dialog';
import { selectCollaborativeEditingAPIKey, selectEditorApiKey } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectDirectionality } from '@src/suite/common/store/hello-results/hello-results.selectors';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { StoryActions } from '@src/suite/common/store/story/actions/story.actions';
import { AnchorCommand, DurationCommand, InsertCommand, NotesCommand, ProductionCommand, ReadRateCommand, TakeCommand } from '@src/suite/common/store/story/models/story.models';
import { convertHtmlToSimplifiedHtml } from '@src/suite/common/utils/html-simplify.utils';
import { isStoryInRundown } from '@src/suite/common/utils/record-utils';
import { parseProductionCommand } from '@src/suite/common/utils/story-utils';
import { convertDurationToENPSClientFormat } from '@src/suite/common/utils/timing-format';
import { KeyName } from '@src/suite/common/utils/util';
import { parseXml } from '@src/suite/common/utils/xml-utils';
import { FroalaEditorModule } from 'angular-froala-wysiwyg';
import FroalaEditor from 'froala-editor';
import { DateTime } from 'luxon';
import { catchError, finalize, interval, Observable, Subject, timer } from 'rxjs';
import { filter, map, switchMap, take, takeUntil, tap, throttle } from 'rxjs/operators';
import { v4 } from 'uuid';

import { ApplicationActions } from '../../store/navigation/actions/application.actions';
import { StoriesRecordActions } from '../../store/records/records.actions';
import { I18nKeys } from '../../utils/i18n.default';
import { SidecarSelectorComponent } from './sidecar-selector/sidecar-selector';

declare const Codox: any;

enum BlackLinePosition {
    TOP = 'top',
    MIDDLE = 'middle',
    BOTTOM = 'bottom',
}

enum EditorPostion {
    ABOVE = 'above',
    BELOW = 'below',
}

interface ProductionCommandCreateEvent {
    productionCommadType: ProductionCommandType;
    pointerProductionCommand: number;
    guid: string;
    editorPosition: EditorPostion;
}

const SIDECAR_SELECTOR_POSITIONS: ConnectedPosition[] = [
    // Primary: to the right
    {
        originX: 'end',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'top',
        offsetX: 8,
    },
    // Fallback 1: to the left
    {
        originX: 'start',
        originY: 'top',
        overlayX: 'end',
        overlayY: 'top',
        offsetX: -8,
    },
    // Fallback 2: below
    {
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
        offsetY: 8,
    },
    // Fallback 3: above
    {
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom',
        offsetY: -8,
    },
];

const CODOX_RETRY_TIMEOUT_MS = 5000;

@Component({
    selector: 'st-rich-text-editor',
    imports: [FroalaEditorModule, MatIcon, MatIconButton],
    templateUrl: './st-rich-text-editor.html',
    styleUrl: './st-rich-text-editor.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-rich-text-editor',
    },
})
export class RichTextEditorComponent implements OnInit, OnDestroy, AfterViewInit {
    BlackLinePositionEnum = BlackLinePosition;
    EditorPostion = EditorPostion;
    private destroy$ = new Subject<void>();

    comments: Record<string, string> = {};

    private elementRef = inject(ElementRef);
    private mosEventService = inject(MosEventService);
    private nomWebServicesJson = inject(NomWebServicesJson);
    private globalTablesService = inject(GlobalTablesService);
    private storeActions$ = inject(Actions);
    private overlay = inject(Overlay);

    dialog = inject(MatDialog);
    ngZone = inject(NgZone);
    modalService = inject(ModalService);
    store = inject(Store);

    readonly aboveBlackLine = viewChild<ElementRef<HTMLDivElement>>('aboveBlackLine');
    readonly belowBlackLine = viewChild<ElementRef<HTMLDivElement>>('belowBlackLine');

    languageSettings = this.store.selectSignal(selectI18N)();

    readonly focused = output<FocusEvent>();
    readonly mosItemOpen = output<MosEditorEvent>();
    readonly mosMediaClicked = output<string>();

    readonly selectedTextChange = output<string>();
    readonly contentChanged = output<EnpsField[]>();
    readonly save = output<string>();
    readonly lockStory = output<boolean>();
    readonly collabUsers = output<{ id: string }[]>();

    currentCommandDialogRef: MatDialogRef<ProductionCommandDialogComponent, any> = null;
    private pendingCursorPositionCmdId: string = null;

    public readonly story = input<StorytellingStoryRecord | undefined>(null);
    public readonly disabled = input(false, {
        transform: coerceBooleanProperty,
    });
    readonly autoUpdate = input(false, {
        transform: coerceBooleanProperty,
    });
    readonly hasUnsavedChanges = input(false);

    public readonly editorAboveBL = signal<FroalaEditor>(null);
    public readonly editorBelowBL = signal<FroalaEditor>(null);
    public readonly editorAboveBLFocused = signal<boolean>(false);
    public readonly editorBelowBLFocused = signal<boolean>(false);

    readonly codoxApiKey = computed(() => {
        return this.store.selectSignal(selectCollaborativeEditingAPIKey)();
    });

    // EditorApiKey
    readonly editorApiKey = computed(() => {
        return this.store.selectSignal(selectEditorApiKey)();
    });

    readonly isStoryInRO = computed(() => {
        return isStoryInRundown(this.story());
    });

    readonly collaborationEnabled = computed(() => {
        return this.story().isWriteable && this.codoxApiKey() && !this.story()?.isNewRecord && !this.isStoryInRO();
    });

    readonly currentEditor = computed(() => {
        // lastActiveEditorPosition is set on focus only because currentEditor() should not change on blur, otherwise we would lose the reference to the editor
        return this.lastActiveEditorPosition() === EditorPostion.BELOW ? this.editorBelowBL() : this.editorAboveBL();
    });
    readonly storyText = computed(() => {
        return this.editorAboveBL().html.get() + blackLineSpan + this.editorBelowBL().html.get();
    });
    readonly showBanner = computed(() => {
        return this.collaborationEnabled() && this.reachMaxCollabUsers();
    });
    readonly isEditorDisabled = computed(() => {
        return this.disabled() || (this.collaborationEnabled() && this.reachMaxCollabUsers());
    });

    readonly circleContainer = computed(() => {
        return this.currentPosition() === BlackLinePosition.MIDDLE || this.currentPosition() === BlackLinePosition.BOTTOM;
    });

    editorAboveBLBlur = () => {
        if (this.editorAboveBLFocused()) {
            this.editorAboveBL().events.trigger('blur', null);
        }
    };

    editorBelowBLBlur = () => {
        if (this.editorBelowBLFocused()) {
            this.editorBelowBL().events.trigger('blur', null);
        }
    };

    readonly loadingData = signal(true);
    readonly contentAboveBL = signal<string>(null);
    readonly contentBelowBL = signal<string>(null);
    readonly hasContentBelowBL = signal<boolean>(false);
    readonly initialMap = signal<Map<string, string>>(new Map());
    readonly currentPosition = signal<BlackLinePosition>(BlackLinePosition.BOTTOM);
    readonly lastActiveEditorPosition = signal<EditorPostion>(EditorPostion.ABOVE);
    editorHeight = 0;
    private saveEnabled = false;
    userHasInteracted = false;
    private isProcessingContentChange = false;
    private cursorRestorePending = false;
    isDraggingExistingCommand = false;
    isDisabledArea = false;
    clearPreviousSelection = false;
    private selectedTextSubject = new Subject<string>();
    options = {
        htmlAllowedEmptyTags: HTML_ALLOWED_EMPTY_TAGS,
        pluginsEnabled: ['link', 'url', 'paragraphFormat'],
        htmlAllowedAttrs: htmlAllowedAttrs,
        key: this.editorApiKey(),
        toolbarSticky: false,
        tooltips: false,
        enter: FroalaEditor.ENTER_BR,
        direction: this.store.selectSignal(selectDirectionality)(),
        events: {
            initialized: (e: FroalaEditor) => {
                const editor = e.getEditor();
                this.editorAboveBL.set(editor);
                if (editor) {
                    if (this.isEditorDisabled()) {
                        editor.edit.off();
                    }

                    this.applyEditorEventListeners(editor);

                    editor.events.on(
                        'focus',
                        event => {
                            this.editorAboveBLFocused.set(true);
                            this.editorBelowBLFocused.set(false);
                            this.focused.emit(event);
                            this.lastActiveEditorPosition.set(EditorPostion.ABOVE);
                            /**
                             * Froala is parsing the content and that is why we are setting the content on first focus.
                             * This way we are preventing misslieading differences betweeen the story text and the Froala content
                             */
                            this.setInitialContentMap();
                        },
                        true
                    );
                    editor.events.on(
                        'blur',
                        _ => {
                            this.ngZone.run(() => {
                                this.selectedTextChange.emit('');
                                this.clearPreviousSelection = false;
                            });
                            this.editorAboveBLFocused.set(false);
                            this.currentEditor().selection.save();
                        },
                        true
                    );
                    editor.el?.addEventListener('blur', this.editorAboveBLBlur);

                    editor.events.on(
                        'commands.before',
                        (cmd, param1, param2) => {
                            // Set userHasInteracted for content-modifying commands (exclude save, selection, etc.)
                            const nonContentCommands = ['save', 'selectAll', 'html', 'getPDF', 'print'];
                            if (!nonContentCommands.includes(cmd)) {
                                this.userHasInteracted = true;
                            }

                            if (this.editorBelowBLFocused()) {
                                this.editorBelowBL().commands.exec(cmd, [param1, param2]);
                                return false;
                            }
                        },
                        true
                    );
                }
                this.editorAboveBL.set(editor);
                this.setSaveButtonState(true);
                if (this.editorAboveBL() && this.collaborationEnabled()) {
                    // will initialize collaboration separately for above and below black line editors, to allow better recovery
                    // and we need to stop any existing collaboration first, reset the retry count and then start it
                    this.stopCollaborationAboveBL();
                    this.startCollaborationAboveBL();
                }
            },
            /**
             * When a link is clicked inside the editor, the default behavior is to follow the link.
             * We prevent this by calling preventDefault() and open the link in a new tab instead.
             * This is done to prevent the user from leaving the story edit page.
             */
            click: clickEvent => {
                if (clickEvent.target && clickEvent.target.tagName.toLowerCase() === 'a') {
                    clickEvent.preventDefault();
                    const href = clickEvent.target.getAttribute('href');
                    if (href) {
                        window.open(href, '_blank');
                    }
                }
                /* EDITOR.COMMENTS: Left for reference in the future, when we start implementing the comments
                else if (clickEvent.target.classList.contains('comment-highlight')) {
                    const commentId = clickEvent.target.getAttribute('data-comment');
                    console.warn(commentId);
                }
                */
            },
            // Intentional disable of linter rule for naming convention as 'paste.before' is Froala's event name
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'paste.before': (clipboardEvent: ClipboardEvent) => {
                // Before pasting content, we need to check if the content contains links
                // If it does, we need to insert the content ourselves, otherwise the links will not be clickable
                const clipboardData = clipboardEvent.clipboardData;
                const pastedHtml = clipboardData.getData('Text')?.trim();
                const linkRegex = new RegExp(/<a.*>.*<\/a>/gi);
                if (pastedHtml && pastedHtml.match(linkRegex)) {
                    this.userHasInteracted = true;
                    // Insert the content ourselves
                    this.editorAboveBL().html.insert(pastedHtml);
                    // Return false to prevent the default paste behavior
                    return false;
                }
            },
            // Intentional disable of linter rule for naming convention as 'paste.beforeCleanup' is Froala's event name
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'paste.beforeCleanup': (clipboardHtml: string) => {
                const simplifiedHtml = convertHtmlToSimplifiedHtml(clipboardHtml);

                return simplifiedHtml;
            },
        },
        toolbarButtons: {
            ...FROALA_DEFAULT_TOOLBAR_ITEMS,
        },
    };

    optionsBelowBlackLine = {
        ...this.options,
        events: {
            ...this.options.events,
            initialized: (e: FroalaEditor) => {
                const editor = e.getEditor();
                if (editor) {
                    editor.toolbar.hide();
                    if (this.isEditorDisabled()) {
                        editor.edit.off();
                    }
                    this.applyEditorEventListeners(editor);

                    editor.events.on(
                        'focus',
                        event => {
                            this.editorAboveBLFocused.set(false);
                            this.editorBelowBLFocused.set(true);
                            this.lastActiveEditorPosition.set(EditorPostion.BELOW);
                            this.focused.emit(event);
                            /**
                             * Froala is parsing the content and that is why we are setting the content on first focus.
                             * This way we are preventing misslieading differences betweeen the story text and the Froala content
                             */
                            this.setInitialContentMap();
                        },
                        true
                    );
                    editor.events.on(
                        'blur',
                        _ => {
                            this.ngZone.run(() => {
                                this.selectedTextChange.emit('');
                                this.clearPreviousSelection = false;
                            });
                            this.editorBelowBLFocused.set(false);
                            this.currentEditor().selection.save();
                        },
                        true
                    );
                    editor.el?.addEventListener('blur', this.editorBelowBLBlur);
                    this.editorBelowBL.set(editor);
                    if (this.editorBelowBL() && this.collaborationEnabled()) {
                        // will initialize collaboration separately for above and below black line editors, to allow better recovery
                        // and we need to stop any existing collaboration first, reset the retry count and then start it
                        this.stopCollaborationBelowBL();
                        this.startCollaborationBelowBL();
                    }
                }
            },
        },
    };

    // Collaboration
    currentStoryGuid = '';
    currentParentGuid = '';
    private collaborationContentSet = false;
    private codoxPluginAboveBLAvailable = false;
    private codoxPluginBelowBLAvailable = false;
    private codoxPluginAboveBL = null;
    private codoxPluginBelowBL = null;
    private codoxAboveBLRetry$ = new Subject<void>();
    private codoxBelowBLRetry$ = new Subject<void>();
    private codoxAboveBLRetryCount = 0;
    private codoxBelowBLRetryCount = 0;
    private originalConsoleError: ((message?: unknown, ...optionalParams: unknown[]) => void) | null = null;
    private readonly MAX_CODOX_RETRIES = 3;
    readonly reachMaxCollabUsers = signal(false);

    stopCollaborationAboveBL() {
        if (this.codoxPluginAboveBL) {
            this.codoxPluginAboveBL.stop();
        }
        this.codoxPluginAboveBL = null;
        this.codoxPluginAboveBLAvailable = false;
        this.reachMaxCollabUsers.set(false);
    }

    stopCollaborationBelowBL() {
        if (this.codoxPluginBelowBL) {
            this.codoxPluginBelowBL.stop();
        }
        this.codoxPluginBelowBL = null;
        this.codoxPluginBelowBLAvailable = false;
    }

    stopCollaboration() {
        this.cleanupConsoleErrorInterceptor();
        this.stopCollaborationAboveBL();
        this.stopCollaborationBelowBL();
        this.collabUsers.emit([]);
    }

    restartCollaboration() {
        this.stopCollaboration();
        // Reset retry streams by setting the retry counts to 0
        this.codoxAboveBLRetryCount = 0;
        this.codoxBelowBLRetryCount = 0;
        this.setupConsoleErrorInterceptor();
        this.startCollaborationAboveBL();
        this.startCollaborationBelowBL();
    }

    private setupRetryStreams() {
        // Setup retry stream for above black line editor
        this.codoxAboveBLRetry$
            .pipe(
                filter(() => !this.codoxPluginAboveBLAvailable),
                switchMap(() => {
                    this.codoxAboveBLRetryCount++;
                    console.warn(`Codox console error detected, attempt above black line (attempt ${this.codoxAboveBLRetryCount}/${this.MAX_CODOX_RETRIES})`);
                    const delay = Math.pow(2, this.codoxAboveBLRetryCount - 1) * 1000;
                    console.warn(`Retrying Codox initialization for above black line in ${delay}ms...`);
                    return timer(delay);
                }),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                if (!this.codoxPluginAboveBLAvailable) {
                    this.stopCollaborationAboveBL();
                    this.startCollaborationAboveBL();
                }
            });

        // Setup retry stream for below black line editor
        this.codoxBelowBLRetry$
            .pipe(
                filter(() => !this.codoxPluginBelowBLAvailable),
                switchMap(() => {
                    this.codoxBelowBLRetryCount++;
                    console.warn(`Codox console error detected, attempt below black line (attempt ${this.codoxBelowBLRetryCount}/${this.MAX_CODOX_RETRIES})`);
                    const delay = Math.pow(2, this.codoxBelowBLRetryCount - 1) * 1000;
                    console.warn(`Retrying Codox initialization for below black line in ${delay}ms...`);
                    return timer(delay);
                }),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                if (!this.codoxPluginBelowBLAvailable) {
                    this.stopCollaborationBelowBL();
                    this.startCollaborationBelowBL();
                }
            });
    }

    retryTimeout = null;
    startCollaborationAboveBL() {
        try {
            // we need to ensure that the editor is initialized and the content is set before starting collaboration
            if (this.editorAboveBL() && this.codoxApiKey() && this.collaborationContentSet) {
                const name = this.globalTablesService.getUserNameByIDV2(this.nomWebServicesJson.UserId);

                const initialize = () => {
                    if (this.codoxPluginAboveBL) {
                        this.codoxPluginAboveBL.stop();
                    }
                    this.codoxPluginAboveBL = new Codox();
                    console.warn('Starting Codox above black line...');
                    this.codoxPluginAboveBL.init({
                        app: 'froala',
                        user: { id: this.nomWebServicesJson.UserId, name: name },
                        docId: `${this.story()?.Guid}_above`,
                        apiKey: this.codoxApiKey(),
                        editor: this.editorAboveBL(),
                        showUI: false,
                        showCursor: true,
                        cursorAlwaysOn: true,
                        hooks: {
                            // invoked whenever a user joins or leaves the session
                            usersUpdate: (data: { id: string }[]) => {
                                if (this.retryTimeout) {
                                    clearTimeout(this.retryTimeout);
                                    this.retryTimeout = null;
                                }
                                // if we ever get users update, it means that the initialization was successful
                                if (!this.codoxPluginAboveBLAvailable) {
                                    console.warn('Codox above black line initialized.');
                                    this.codoxPluginAboveBLAvailable = true;
                                }
                                const lockUserExists = data.some(codoxUser => {
                                    return codoxUser.id === this.story().EditLockedBy;
                                });
                                if (!lockUserExists) {
                                    this.lockStory.emit(true);
                                }
                                this.collabUsers.emit(data);
                            },
                            contentChanged: data => {
                                if (data?.source === 'remote') {
                                    this.userHasInteracted = true;
                                }
                            },
                        },
                    });

                    this.codoxPluginAboveBL.on('error', data => {
                        if (data && data.message.includes(CodoxError.SESSION_FULL)) {
                            this.reachMaxCollabUsers.set(true);
                        }
                    });
                };

                if (this.retryTimeout) {
                    clearTimeout(this.retryTimeout);
                    this.retryTimeout = null;
                }
                this.retryTimeout = setTimeout(() => {
                    if (this.retryTimeout) {
                        clearTimeout(this.retryTimeout);
                        this.retryTimeout = null;
                    }
                    if (!this.codoxPluginAboveBLAvailable && !this.reachMaxCollabUsers()) {
                        console.warn('Codox above black line not initialized yet, retrying...');
                        initialize();
                    }
                }, CODOX_RETRY_TIMEOUT_MS);
                initialize();
            }
        } catch (error) {
            console.error('Codox above black line initialization error:', error);
            this.codoxPluginAboveBLAvailable = false;
        }
    }

    startCollaborationBelowBL() {
        try {
            // we need to ensure that the editor is initialized and the content is set before starting collaboration
            if (this.editorBelowBL() && this.codoxApiKey() && this.collaborationContentSet) {
                const name = this.globalTablesService.getUserNameByIDV2(this.nomWebServicesJson.UserId);
                this.codoxPluginBelowBL = new Codox();
                this.codoxPluginBelowBL.on('error', data => {
                    console.error('codoxPluginBelowBlackLine', data);
                });
                console.warn('Starting Codox below black line...');
                this.codoxPluginBelowBL.init({
                    app: 'froala',
                    user: { name: name, id: this.nomWebServicesJson.UserId },
                    docId: `${this.story()?.Guid}_below`,
                    apiKey: this.codoxApiKey(),
                    editor: this.editorBelowBL(),
                    showUI: false,
                    showCursor: true,
                    cursorAlwaysOn: true,
                    // invoked whenever a user joins or leaves the session
                    // use it to detect if the initicalization was successful, because it triggers only after successful init for the current user
                    hooks: {
                        usersUpdate: () => {
                            if (!this.codoxPluginBelowBLAvailable) {
                                console.warn('Codox below black line initialized.');
                                this.codoxPluginBelowBLAvailable = true;
                            }
                        },
                    },
                });
            }
        } catch (error) {
            console.error('Codox below black line initialization error:', error);
        }
    }

    /**
     * Sets up an interceptor for console.error to catch Codox-related errors
     * and implement a retry mechanism with exponential backoff.
     * This is used to handle cases where the Codox plugin fails to initialize, and we want to retry initialization.
     */
    private setupConsoleErrorInterceptor() {
        // Store original console.error
        this.originalConsoleError = console.error;
        console.warn('Setting up console error interceptor for Codox');

        // Override console.error to intercept Codox errors
        console.error = (...args: unknown[]) => {
            // Call original console.error first
            this.originalConsoleError?.apply(console, args);

            // Check if this is a Codox error
            const errorMessage = args.join(' ');
            if (errorMessage.includes('codox_handleMessage') || errorMessage.includes('ESTOP') || errorMessage.includes('wave.client.js')) {
                // Trigger retry streams using RxJS
                if (!this.codoxPluginAboveBLAvailable && this.codoxAboveBLRetryCount < this.MAX_CODOX_RETRIES) {
                    this.codoxAboveBLRetry$.next();
                }

                if (!this.codoxPluginBelowBLAvailable && this.codoxBelowBLRetryCount < this.MAX_CODOX_RETRIES) {
                    this.codoxBelowBLRetry$.next();
                }
            }
        };
    }

    private cleanupConsoleErrorInterceptor() {
        if (this.originalConsoleError) {
            console.error = this.originalConsoleError;
            this.originalConsoleError = null;
        }
    }
    // END Collaboration

    constructor() {
        const productionButtons = [];
        PRODUCTION_COMMAND_BUTTONS.forEach(command => {
            productionButtons.push(command);
        });
        this.setupRetryStreams();

        this.options = {
            ...this.options,
            toolbarButtons: {
                ...this.options.toolbarButtons,
                moreProductionCommands: {
                    buttons: productionButtons,
                    align: 'left',
                    buttonsVisible: 6,
                },
            },
        };

        effect(() => {
            // ;( all the magic is happening here, I'm affraid to touch it, but will have to.
            // with the great power comes great responsibility
            if (this.editorAboveBL() && this.editorBelowBL()) {
                if (this.isEditorDisabled()) {
                    this.editorAboveBL().edit.off();
                    this.editorBelowBL().edit.off();
                } else {
                    this.editorAboveBL().edit.on();
                    this.editorBelowBL().edit.on();
                }
                // Clear cursor restore pending if story changed
                if (this.cursorRestorePending && this.story()?.Guid !== this.currentStoryGuid) {
                    this.cursorRestorePending = false;
                }
                if (
                    !this.cursorRestorePending &&
                    (!this.collaborationEnabled() || !this.story()?.fromRTU) &&
                    !this.story().containsPartialText &&
                    ((!this.editorAboveBLFocused() && !this.editorBelowBLFocused()) || this.isEditorDisabled() || this.story()?.Guid !== this.currentStoryGuid)
                ) {
                    this.currentStoryGuid = this.story()?.Guid;
                    this.userHasInteracted = false;
                    if (this.story() != null && this.story()?.text != null) {
                        const text = this.story()?.text?.trim();
                        const contents = text?.split(blackLineRegex);
                        this.contentAboveBL.set(contents?.[0] || '');
                        this.contentBelowBL.set(contents?.[2] || '');
                        this.hasContentBelowBL.set(this.contentBelowBL()?.trim()?.length > 0);
                    } else {
                        this.contentAboveBL.set('');
                        this.contentBelowBL.set('');
                        this.hasContentBelowBL.set(false);
                    }
                }
                this.setSaveButtonState(this.story()?.isNewRecord || !this.hasUnsavedChanges());
                // restore pointer after save
                if (!this.hasUnsavedChanges() && !this.saveEnabled && this.cursorRestorePending) {
                    this.currentEditor()?.selection?.restore();
                    this.cursorRestorePending = false;
                }
            }
        });

        effect(() => {
            if (!this.story().containsPartialText && this.story()?.Guid && (this.story().Guid !== this.currentStoryGuid || this.story()?.Path?.parentCollectionGuid !== this.currentParentGuid)) {
                this.currentStoryGuid = this.story().Guid;
                this.currentParentGuid = this.story()?.Path?.parentCollectionGuid;
                this.userHasInteracted = false;
                if (this.collaborationEnabled()) {
                    if (this.story() != null && this.story()?.text != null) {
                        const text = this.story()?.text?.trim();
                        const contents = text?.split(blackLineRegex);
                        this.contentAboveBL.set(contents?.[0] || '');
                        this.contentBelowBL.set(contents?.[2] || '');
                        this.hasContentBelowBL.set(this.contentBelowBL()?.trim()?.length > 0);
                    } else {
                        this.contentAboveBL.set('');
                        this.contentBelowBL.set('');
                        this.hasContentBelowBL.set(false);
                    }
                    // We need this flag to ensure that the content is set with the one from the story before starting collaboration
                    this.collaborationContentSet = true;
                    this.restartCollaboration();
                } else {
                    this.stopCollaboration();
                }
            }
        });

        effect(() => {
            if (this.story()?.Guid && this.pendingCursorPositionCmdId && !this.isEditorDisabled()) {
                this.restorePendingCursorPosition();
            }
        });
    }

    ngOnInit(): void {
        this.setupConsoleErrorInterceptor();
        this.mosEventService
            .onMosItem()
            .pipe(takeUntil(this.destroy$))
            .subscribe(({ storyGuid, mosCommand }) => {
                if (this.story()?.Guid === storyGuid) {
                    this.addMosItemToStoryListener(mosCommand);
                }
            });
        this.mosEventService
            .onMosItemEdit()
            .pipe(takeUntil(this.destroy$))
            .subscribe(({ storyGuid, mosCommand, mosItemId: mosElId }) => {
                if (this.story()?.Guid === storyGuid) {
                    this.editMosItemInStoryListener(mosCommand, mosElId);
                }
            });

        this.selectedTextSubject
            .pipe(
                throttle(
                    () => {
                        const throttleInterval = environment.read_rate_time_throttle_interval_seconds ? environment.read_rate_time_throttle_interval_seconds * 1000 : 1000;
                        return interval(throttleInterval);
                    },
                    {
                        leading: true,
                        trailing: true,
                    }
                ),
                takeUntil(this.destroy$)
            )
            .subscribe(selectedText => {
                this.selectedTextChange.emit(selectedText);
            });
    }

    ngAfterViewInit(): void {
        this.storeActions$
            .pipe(
                ofType(StoryActions.focusEditor),
                filter(action => action.storyGuid === this.story().Guid),
                tap(() => {
                    this.editorAboveBL()?.events?.focus();
                }),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this.stopCollaboration();
        this.destroy$.next();
        this.destroy$.complete();
        this.editorAboveBL()?.el?.removeEventListener('blur', this.editorAboveBLBlur);
        this.editorBelowBL()?.el?.removeEventListener('blur', this.editorBelowBLBlur);
    }

    private resolveCurrentMirId(): string | null {
        const focusedEditor = this.currentEditor();
        if (!focusedEditor?.el) return null;

        const allMirs = Array.from(focusedEditor.el.getElementsByClassName('st-rich-text-editor--mos')) as HTMLElement[];
        if (!allMirs.length) return null;

        const selection = window.getSelection();
        if (!selection || !selection.rangeCount) return allMirs[0].id || null;

        const range = selection.getRangeAt(0);
        const cursorNode = range.startContainer;

        // Find the first MIR that appears after the cursor in document order
        const mirAfterCursor = allMirs.find(mir => !!(cursorNode.compareDocumentPosition(mir) & Node.DOCUMENT_POSITION_FOLLOWING));
        if (mirAfterCursor?.id) return mirAfterCursor.id;

        // Fall back to the last MIR that appears before the cursor
        const lastMirBeforeCursor = [...allMirs].reverse().find(mir => !!(cursorNode.compareDocumentPosition(mir) & Node.DOCUMENT_POSITION_PRECEDING));
        return lastMirBeforeCursor?.id || allMirs[0].id || null;
    }

    @HostListener('click', ['$event.target'])
    onClick(element: EventTarget) {
        this.hostClickListener(element as Element);
    }

    @HostListener('dragstart', ['$event'])
    onDragStart(event: DragEvent) {
        event.dataTransfer?.setData('text/plain', (event.target as HTMLElement).id);
        event.dataTransfer!.dropEffect = 'move';
        if ((event.target as HTMLElement).id) {
            this.isDraggingExistingCommand = true;
        }
    }

    @HostListener('window:beforeunload')
    onBeforeUnload() {
        this.stopCollaboration();
    }

    isInitialContentChanged = false;
    setInitialContentMap() {
        const storyGuid = this.story()?.Guid;
        if (storyGuid && !this.initialMap()?.has(storyGuid)) {
            this.isInitialContentChanged = false;
            this.initialMap().clear();
            this.initialMap().set(storyGuid, this.storyText() ? this.storyText() : '');
        }
    }

    ensureCursorVisibility(editor) {
        if (editor.core.hasFocus() && editor.browser.mozilla) {
            editor.events.disableBlur();
            setTimeout(() => {
                editor.$el.blur().focus();
                editor.events.enableBlur();
            }, 0);
        }
    }

    removeCommandObject(commandIds: string[]) {
        const newStory = { ...this.story() };
        const textCommands = { ...newStory.TextCommands };
        // Remove elements from DOM and get updated content
        for (const id of commandIds) {
            const element = this.currentEditor()?.el?.querySelector(`#${CSS.escape(id)}`);
            if (element) {
                // Also remove empty paragraph after the command if it exists
                const nextElement = element.nextElementSibling;
                if (nextElement && nextElement.tagName.toLowerCase() === 'p' && !nextElement.textContent?.trim()) {
                    nextElement.remove();
                }
                element.remove();
            }
            delete textCommands[id];
        }

        // Update editor content after DOM manipulation
        const updatedContent = this.currentEditor().html.get();
        if (this.editorBelowBLFocused()) {
            if (this.codoxPluginBelowBLAvailable) {
                this.codoxPluginBelowBL?.forceSync(updatedContent);
            } else {
                this.contentBelowBL.set(updatedContent);
            }
            newStory.text = `${this.contentAboveBL()}${blackLineSpan}${updatedContent}`;
        } else {
            if (this.codoxPluginAboveBLAvailable) {
                this.codoxPluginAboveBL?.forceSync(updatedContent);
            } else {
                this.contentAboveBL.set(updatedContent);
            }
            newStory.text = `${updatedContent}${blackLineSpan}${this.contentBelowBL()}`;
        }

        newStory.TextCommands = textCommands;
        this.handleContentChanged(newStory);
    }

    handleContentChanged(newStory: StorytellingStoryRecord) {
        this.emitContentChanged([
            {
                key: PSTFieldNames.PSTRichText,
                value: newStory.text,
            },
            {
                key: PSTFieldNames.TextCommands,
                value: newStory.TextCommands,
            },
        ]);
    }

    expandAllElements() {
        const content = this.storyText().replaceAll('expand_less', 'expand_more').replaceAll('--hidden', '');
        this.editorAboveBL().html.set(content.split(blackLineRegex)[0] || '');
        this.editorBelowBL().html.set(content.split(blackLineRegex)[2] || '');
    }

    openSidecarChooser(anchorElement: Element, metadata: { type: 'mos' | 'production'; mosId: string; mosPluginId: string; storyGuid: string }) {
        const { mosId, mosPluginId, storyGuid } = metadata;
        const positionStrategy = this.overlay.position().flexibleConnectedTo(anchorElement).withPositions(SIDECAR_SELECTOR_POSITIONS).withFlexibleDimensions(false).withPush(false);
        // Optional, prevents overlay from pushing into view if it doesn't fit

        const overlayRef = this.overlay.create({
            positionStrategy,
            hasBackdrop: true,
            backdropClass: 'st-sidecar-selector-backdrop',
            disposeOnNavigation: true,
            scrollStrategy: this.overlay.scrollStrategies.reposition(),
        });

        const sidecarPortal = new ComponentPortal(SidecarSelectorComponent);
        const sidecarSelectorComponent = overlayRef.attach(sidecarPortal);

        sidecarSelectorComponent.instance.layoutSelected.subscribe(event => {
            this.mosItemOpen.emit({
                mosId,
                mosPluginId,
                storyGuid,
                layout: event.layout,
                sidecar: event.sidecar,
            });
            overlayRef.dispose();
        });
        overlayRef
            .keydownEvents()
            .pipe(
                filter(event => event.key === KeyName.Escape),
                take(1),
                map(() => {
                    overlayRef.dispose();
                    return false;
                })
            )
            .subscribe();
        overlayRef
            .backdropClick()
            .pipe(
                take(1),
                map(() => {
                    overlayRef.dispose();
                    return false;
                })
            )
            .subscribe();
    }

    hostClickListener(element: Element) {
        if (!element) return;

        // TODO: move this and dragstart to a mutation observer, for NED
        if (element.classList.contains('st-rich-text-editor--production-close')) {
            this.removeProductionObject(element);
            return;
        } else if (element.classList.contains('st-rich-text-editor--mos-open-external')) {
            const mosElement = element.closest('.st-rich-text-editor--mos');
            if (mosElement) {
                const mosPluginId = mosElement.getAttribute('mosid');
                this.openSidecarChooser(element, { type: 'mos', mosId: mosElement.id, mosPluginId, storyGuid: this.story()?.Guid });
                return;
            }
        }
        if (element.classList.contains('st-rich-text-editor--mos-media')) {
            const mosElement = element.closest('.st-rich-text-editor--mos');
            if (mosElement) {
                const mosId = mosElement.id;
                this.mosMediaClicked.emit(mosId);
                return;
            }
        }
        if (element.classList.contains('st-rich-text-editor--settings')) {
            const commandElementHtml = element.closest('.st-rich-text-editor--production');
            this.onProductionCommandEdit(commandElementHtml?.id);
            return;
        }
        const productionCommandExpand = element.closest('.st-rich-text-editor--production-expander');
        if (productionCommandExpand) {
            const expanderIcon = productionCommandExpand.textContent;
            productionCommandExpand.textContent = expanderIcon === 'expand_more' ? 'expand_less' : 'expand_more';
            const prodCommandParent = productionCommandExpand.closest('.st-rich-text-editor--production');
            const mosCommandParent = productionCommandExpand.closest('.st-rich-text-editor--mos');
            if (mosCommandParent) {
                this.toggleDetails(mosCommandParent, 'st-rich-text-editor--mos-details');
            } else if (prodCommandParent) {
                this.toggleDetails(prodCommandParent, 'st-rich-text-editor--production-details');
                this.toggleDetails(prodCommandParent, 'st-rich-text-editor--production-details--notes');
            }
            return;
        }
    }

    toggleDetails(parentElement, detailsSelector) {
        const detailsElement = parentElement.querySelector('.' + detailsSelector);
        if (detailsElement) {
            detailsElement.classList.toggle(detailsSelector + '--hidden');
        }
    }

    removeProductionObject(element: Element) {
        this.confirmApprovedStoryEdit(() => {
            this.handleRemoveProductionObject(element);
        });
    }

    private handleRemoveProductionObject(element: Element) {
        this.modalService
            .openModal({
                title: `${this.languageSettings.PSTWebConfirmDeletePart1} item?`,
                content: '',
                yesString: this.languageSettings.PSTWebOkButtonLabel,
                noString: this.languageSettings.PSTWebCancelButtonLabel,
            })
            .result.pipe(
                take(1),
                catchError(() => {
                    return [ModalResponseCodes.CANCEL];
                })
            )
            .subscribe(res => {
                if (res === ModalResponseCodes.OK) {
                    const mosElementParent = element.closest('.st-rich-text-editor--mos');
                    const commandElementParent = element.closest('.st-rich-text-editor--production');
                    const commandElementId = mosElementParent?.id || commandElementParent?.id;
                    this.removeCommandObject([commandElementId]);
                }
            });
    }

    onInitialContentChanged(storyText?: string) {
        if (!this.isInitialContentChanged) {
            /**
             * On every BasicContent we are returning different production command id,
             * so we need to remove it before comparing the froala's content change event and initial content.
             */
            const content = storyText.replace(new RegExp('id="[^"]*"', 'g'), "id=''");
            const initialText = this.initialMap().get(this.story().Guid)?.replace(new RegExp('id="[^"]*"', 'g'), "id=''");

            if (content === initialText) {
                return;
            }
        }
        this.isInitialContentChanged = true;
    }

    onContentChange(event: string, editorPosition: EditorPostion) {
        // Get the current content from both editors
        const editorAboveContent = this.editorAboveBL().html.get();
        const editorBelowContent = this.editorBelowBL().html.get();
        // Combine content from both editors
        const fullContent = editorAboveContent + blackLineSpan + editorBelowContent;
        this.onInitialContentChanged(fullContent);

        if (!this.isInitialContentChanged) {
            return;
        }

        // Update the appropriate content signal based on which editor is focused and there is an actual change in content, then emit the changes
        if (this.userHasInteracted && !this.isProcessingContentChange) {
            this.userHasInteracted = false;
            this.confirmApprovedStoryEdit(() => {
                this.updateContentAndEmitChanges(event, fullContent, editorPosition);
            });
        } else if (!this.isProcessingContentChange && this.collaborationEnabled()) {
            /*
            this is very special case where we need to update the TextCommands in the story without emitting content changed event, because the content change is triggered by external change (like collaboration or initial content set) and we don't want to override the TextCommands with potentially outdated ones from the editor.
            The problem is that we need to not show the confirm approved when nothing has changed. But it will detect it otherwise.
            So we need to parse the content, get the TextCommands and update the story with the new TextCommand ids
            All that is because of the dynamic ids that we are generating for the production and mos commands.
            */
            const parsedText = parseXml(fullContent, 'text/html');
            if (!parsedText) {
                return;
            }

            const textCommands = {};
            const productionCommandElements = parsedText.getElementsByClassName('st-rich-text-editor--production__content');
            for (let i = 0; i < productionCommandElements?.length; i++) {
                const commandElement = productionCommandElements?.item(i);
                if (!commandElement) {
                    continue;
                }
                const id = commandElement.id?.replace(PRODUCTION_CONTENT_ID_PREFIX, '');
                if (!id) {
                    continue;
                }
                textCommands[id] = `[${commandElement.textContent.replace(new RegExp(PRODUCTION_COMMAND_ROW_SEPARATOR, 'g'), '\r\n')}]`;
            }

            const mosCommandElements = parsedText.getElementsByClassName('st-rich-text-editor--mos__content');
            for (let i = 0; i < mosCommandElements?.length; i++) {
                const commandElement = mosCommandElements?.item(i);
                if (!commandElement) {
                    continue;
                }
                const id = commandElement.id?.replace(MOS_CONTENT_ID_PREFIX, '');
                if (!id) {
                    continue;
                }
                textCommands[id] = `[${decodeURIComponent(commandElement.textContent)}]`;
            }

            // NED: don't like this, but I had to do it for now, because I don't want to trigger content changed event.
            // Other approach could be to emit new event, but I don't want to add more complexity to the component for now.
            this.store.dispatch(StoriesRecordActions.updateStories({ records: [{ Guid: this.story()?.Guid, TextCommands: textCommands }] }));
        }
    }

    private updateContentAndEmitChanges(event: string, fullContent: string, editorPosition: EditorPostion) {
        switch (editorPosition) {
            case EditorPostion.ABOVE:
                if (!this.codoxPluginAboveBLAvailable) {
                    this.contentAboveBL.set(event);
                }
                break;
            case EditorPostion.BELOW: {
                if (!this.codoxPluginBelowBLAvailable) {
                    this.contentBelowBL.set(event);
                }
                const textContent = event ? event.trim() : '';
                this.hasContentBelowBL.set(textContent.length > 0);
                break;
            }
        }

        const parsedText = parseXml(fullContent, 'text/html');
        if (!parsedText) {
            return;
        }

        const textCommands = {};
        const productionCommandElements = parsedText.getElementsByClassName('st-rich-text-editor--production__content');
        for (let i = 0; i < productionCommandElements?.length; i++) {
            const commandElement = productionCommandElements?.item(i);
            if (!commandElement) {
                continue;
            }
            const id = commandElement.id?.replace(PRODUCTION_CONTENT_ID_PREFIX, '');
            if (!id) {
                continue;
            }
            textCommands[id] = `[${commandElement.textContent.replace(new RegExp(PRODUCTION_COMMAND_ROW_SEPARATOR, 'g'), '\r\n')}]`;
        }

        const mosCommandElements = parsedText.getElementsByClassName('st-rich-text-editor--mos__content');
        for (let i = 0; i < mosCommandElements?.length; i++) {
            const commandElement = mosCommandElements?.item(i);
            if (!commandElement) {
                continue;
            }
            const id = commandElement.id?.replace(MOS_CONTENT_ID_PREFIX, '');
            if (!id) {
                continue;
            }
            textCommands[id] = `[${decodeURIComponent(commandElement.textContent)}]`;
        }

        this.emitContentChanged([
            { key: PSTFieldNames.PSTRichText, value: fullContent },
            { key: PSTFieldNames.TextCommands, value: textCommands },
        ]);
    }

    emitContentChanged(fields: EnpsField[]) {
        if (this.autoUpdate() === false) {
            this.setSaveButtonState(this.story()?.isNewRecord || false);
        }
        this.contentChanged.emit(fields);
    }

    onProductionCommandEdit(commandId: string) {
        if (!this.story()?.TextCommands?.[commandId]) {
            return;
        }

        let parsedProdCommand: ProductionCommand | null = null;
        try {
            parsedProdCommand = parseProductionCommand(this.story().TextCommands[commandId], commandId);
        } catch (error) {
            if (error instanceof Error && error.message?.startsWith('Unknown command type')) {
                this.showFailedToParseCommandNotification();
            }
        }

        if (!parsedProdCommand) {
            return;
        }
        this.confirmApprovedStoryEdit(() => {
            this.handleProductionCommandsEdit(parsedProdCommand, commandId);
        });
    }

    private handleProductionCommandsEdit(parsedProductionCommand: ProductionCommand, commandId: string) {
        // Handle ANCHOR/READRATE commands
        if (parsedProductionCommand.commandType === ProductionCommandType.ANCHOR || parsedProductionCommand.commandType === ProductionCommandType.READRATE) {
            this.handleAnchorReadRateCommandOnEdit(parsedProductionCommand, commandId);
        } else {
            // Handle other command types normally
            this.openEditProductionCommandDialog(parsedProductionCommand);
        }
    }

    private openEditProductionCommandDialog(command: ProductionCommand | ProductionCommand[]) {
        if (this.currentCommandDialogRef !== null && this.currentCommandDialogRef.getState() === MatDialogState.OPEN) {
            this.currentCommandDialogRef.close();
        }

        if (Array.isArray(command)) {
            this.currentCommandDialogRef = this.dialog.open(ProductionCommandDialogComponent, {
                hasBackdrop: true,
                panelClass: 'st-production-command-dialog',
                data: {
                    commands: command,
                    commandType: command[0].commandType,
                    editMode: true,
                },
                autoFocus: true,
                delayFocusTrap: true,
            });
        } else {
            this.currentCommandDialogRef = this.dialog.open(ProductionCommandDialogComponent, {
                hasBackdrop: true,
                panelClass: 'st-production-command-dialog',
                data: {
                    command: command,
                    commandType: command.commandType,
                    editMode: true,
                },
                autoFocus: true,
                delayFocusTrap: true,
            });
        }

        // Handle updates for both single commands and arrays
        (this.currentCommandDialogRef.componentInstance.onUpdate as unknown as Observable<ProductionCommand | ProductionCommand[]>)
            .pipe(
                tap(updateCommand => {
                    if (!this.story()?.Guid) {
                        return;
                    }

                    const newStory: StorytellingStoryRecord = { ...this.story() };
                    if (Array.isArray(updateCommand)) {
                        this.handleAnchorReadRateCommandOnUpdate(updateCommand, newStory);
                    } else {
                        this.handleOtherCommandsOnUpdate(updateCommand, newStory);
                    }
                }),
                takeUntil(this.currentCommandDialogRef.componentInstance.destroyed$)
            )
            .subscribe();

        // Handle command deletion
        this.currentCommandDialogRef.componentInstance.onDelete
            .pipe(
                tap(() => {
                    this.removeCommandObject(Array.isArray(command) ? command.map(c => c.guid) : [command.guid]);
                }),
                takeUntil(this.currentCommandDialogRef.componentInstance.destroyed$)
            )
            .subscribe();
    }

    onProductionCommandCreate(event: ProductionCommandCreateEvent) {
        const productionCommand = this.initCommnad(event.productionCommadType);
        if (!productionCommand) {
            return;
        }
        this.confirmApprovedStoryEdit(() => {
            this.handleProductionCommandsCreate(productionCommand, event);
        });
    }

    private handleProductionCommandsCreate(productionCommand: ProductionCommand | ProductionCommand[], event: ProductionCommandCreateEvent) {
        if (this.currentCommandDialogRef !== null && this.currentCommandDialogRef.getState() === MatDialogState.OPEN) {
            this.currentCommandDialogRef.close();
        }

        if (Array.isArray(productionCommand)) {
            this.currentCommandDialogRef = this.dialog.open(ProductionCommandDialogComponent, {
                hasBackdrop: true,
                panelClass: 'st-production-command-dialog',
                data: { commands: productionCommand },
                autoFocus: true,
                delayFocusTrap: true,
            });
        } else {
            this.currentCommandDialogRef = this.dialog.open(ProductionCommandDialogComponent, {
                hasBackdrop: true,
                panelClass: 'st-production-command-dialog',
                data: { command: productionCommand },
                autoFocus: true,
                delayFocusTrap: true,
            });
        }

        if (Array.isArray(productionCommand)) {
            this.handleAnchorReadRateCommandOnInsert(event);
        } else {
            this.handleOtherCommandsOnInsert(event);
        }
    }

    initCommnad(commandType: ProductionCommandType): ProductionCommand | ProductionCommand[] {
        const productionCommandsObjectOptions = ML2HtmlUtils.productionCommandsObjectOptions();
        switch (commandType) {
            case ProductionCommandType.ANCHOR:
            case ProductionCommandType.READRATE:
                return [
                    {
                        commandType: ProductionCommandType.ANCHOR,
                        guid: null,
                        [ProductionFieldName.TEXT]: '',
                    } as AnchorCommand,
                    {
                        commandType: ProductionCommandType.READRATE,
                        guid: null,
                        [ProductionFieldName.TEXT]: '',
                    } as ReadRateCommand,
                ];
            case ProductionCommandType.DURATION:
                return {
                    commandType: commandType,
                    guid: null,
                    [ProductionFieldName.DURATION]: null,
                } as DurationCommand;

            case ProductionCommandType.NOTES:
                return {
                    commandType: commandType,
                    guid: null,
                    [ProductionFieldName.TEXT]: '',
                } as NotesCommand;

            case ProductionCommandType.TAKE:
            case ProductionCommandType.INSERT:
                return {
                    commandType: commandType,
                    guid: null,
                    [ProductionFieldName.OPTION]: productionCommandsObjectOptions[productionCommandsObjectOptions.length - 1].id,
                    [ProductionFieldName.OPTION_DESCRIPTION]: null,
                    [ProductionFieldName.NAME]: null,
                    [ProductionFieldName.NUMBER]: null,
                    [ProductionFieldName.INCUE]: null,
                    [ProductionFieldName.OUTCUE]: null,
                    [ProductionFieldName.AT]: null,
                    [ProductionFieldName.TO]: null,
                    [ProductionFieldName.TIME_VALUE]: null,
                    [ProductionFieldName.TIME_FIELD]: ProductionFieldName.DURATION,
                } as TakeCommand | InsertCommand;

            default:
                throw new Error(`Unknown command type: ${commandType}`);
        }
    }

    convertProductionCommandToText(command: ProductionCommand): string {
        let textLinesArray = [];
        switch (command.commandType) {
            case ProductionCommandType.ANCHOR:
            case ProductionCommandType.READRATE:
                textLinesArray = command[ProductionFieldName.TEXT] ? [`${this.languageSettings[ProductionCommandsLangKey[command.commandType]]}:${command[ProductionFieldName.TEXT]}`] : null;
                break;
            case ProductionCommandType.DURATION:
                textLinesArray = command[ProductionFieldName.DURATION]
                    ? [`${this.languageSettings[ProductionCommandsLangKey[command.commandType]]}:${convertDurationToENPSClientFormat(command[ProductionFieldName.DURATION])}`]
                    : null;
                break;

            case ProductionCommandType.NOTES:
                textLinesArray = command[ProductionFieldName.TEXT]
                    ? [`${this.languageSettings[ProductionCommandsLangKey[command.commandType]]}:${command[ProductionFieldName.TEXT].replace(/\r\n|\n/g, ' ')}`]
                    : null;
                break;

            case ProductionCommandType.TAKE:
            case ProductionCommandType.INSERT: {
                const productionCommandsObjectOptions = ML2HtmlUtils.productionCommandsObjectOptions();
                const lastOptionId = productionCommandsObjectOptions[productionCommandsObjectOptions.length - 1].id;
                textLinesArray = [
                    `${this.languageSettings[ProductionCommandsLangKey[command.commandType]]}${
                        command[ProductionFieldName.OPTION] && command[ProductionFieldName.OPTION] !== lastOptionId
                            ? ' ' + this.languageSettings[command[ProductionFieldName.OPTION]]
                            : `${
                                  command[ProductionFieldName.OPTION_DESCRIPTION] && command[ProductionFieldName.OPTION] === lastOptionId
                                      ? ` :${command[ProductionFieldName.OPTION_DESCRIPTION]}`
                                      : ' :'
                              }`
                    }`,
                    `${command[ProductionFieldName.NAME] ? `${ProductionFieldName.NAME}:${command[ProductionFieldName.NAME]}` : ''}`,
                    `${command[ProductionFieldName.NUMBER] ? `${ProductionFieldName.NUMBER}:${command[ProductionFieldName.NUMBER]}` : ''}`,
                    `${command[ProductionFieldName.INCUE] ? `${ProductionFieldName.INCUE}:${command[ProductionFieldName.INCUE]}` : ''}`,
                    `${command[ProductionFieldName.OUTCUE] ? `${ProductionFieldName.OUTCUE}:${command[ProductionFieldName.OUTCUE]}` : ''}`,
                    `${
                        command[ProductionFieldName.AT] && command[ProductionFieldName.AT] !== INITIAL_TIME_STAMP
                            ? `${ProductionFieldName.AT}:${convertDurationToENPSClientFormat(command[ProductionFieldName.AT])}`
                            : ''
                    }`,
                    `${
                        command[ProductionFieldName.TO] && command[ProductionFieldName.TO] !== INITIAL_TIME_STAMP
                            ? `${ProductionFieldName.TO}:${convertDurationToENPSClientFormat(command[ProductionFieldName.TO])}`
                            : ''
                    }`,
                    `${
                        command[ProductionFieldName.TIME_VALUE] && command[ProductionFieldName.TIME_VALUE] !== INITIAL_TIME_STAMP
                            ? `${command[ProductionFieldName.TIME_FIELD]}:${convertDurationToENPSClientFormat(command[ProductionFieldName.TIME_VALUE])}`
                            : ''
                    }`,
                ];
                break;
            }
            default:
                throw new Error(`Unknown command type: ${command.commandType}`);
        }
        return textLinesArray && textLinesArray.length > 0 ? `[${textLinesArray.filter(val => !!val.trim()).join('\r\n')}]` : '';
    }

    setSaveButtonState(disable: boolean) {
        // TODO: handle both editor and editorBelowBlackLine
        const saveButton = this.editorAboveBL()?.$tb?.find('.fr-command[data-cmd="save"]');
        if (saveButton?.[0]) {
            if (disable) {
                saveButton[0].classList.add('fr-disabled');
                saveButton[0].setAttribute('aria-disabled', 'true');
                saveButton[0].setAttribute('tabindex', '-1');
            } else {
                saveButton[0].classList.remove('fr-disabled');
                saveButton[0].removeAttribute('aria-disabled');
                saveButton[0].setAttribute('tabindex', '0');
            }
            this.saveEnabled = !disable;
        }
    }

    insertProductionCommand(command: ProductionCommandType) {
        this.currentEditor().events.trigger('productionCommand', [command]);
    }

    applyEditorEventListeners(editor: FroalaEditor) {
        editor.events.on('dragover', ev => {
            if (!this.isDraggingExistingCommand && !this.isDisabledArea) {
                ev.preventDefault();
                ev.stopPropagation();
                return;
            }
        });
        // on drag enter to check if the drag is from the MOS plugin, is sameOrigin and check if target element is editable in order to change the cursor default behavior
        editor.events.on('dragenter', ev => {
            this.isDisabledArea = false;

            if (!this.isDraggingExistingCommand) {
                editor.markers.insertAtPoint(ev.originalEvent);
                const currentNode = editor.selection.element();
                this.isDisabledArea = !!currentNode.closest('[contenteditable=false]');
                ev.preventDefault();
                ev.stopPropagation();
                return;
            }
        });
        editor.events.on(
            'drop',
            ev => {
                let data = ev.originalEvent.dataTransfer.getData('text/plain');
                if (typeof data === 'string') {
                    try {
                        data = JSON.parse(data);
                    } catch (e) {
                        console.warn(e);
                    }
                }
                editor.markers.insertAtPoint(ev.originalEvent);
                const $marker = editor.$el.find('.fr-marker');
                $marker.replaceWith(FroalaEditor.MARKERS);
                editor.selection.restore();
                if (!editor.undo.canDo()) editor.undo.saveStep();

                if (data?.type === DRAGTYPES.AttachmentPool || data?.type === DRAGTYPES.MosPluginDragEnd || data?.type === DRAGTYPES.MosPluginDragStart) {
                    const currentNode = editor.selection.element();
                    if (!currentNode.closest('[contenteditable=false]')) {
                        const mosItemElement = ML2HtmlUtils.generateMosItemHtml(data.data, v4());
                        if (mosItemElement) {
                            editor.html.insert(mosItemElement.outerHTML);
                        }
                    }
                } else {
                    const element = document.getElementById(data);
                    if (element) {
                        editor.html.insert(element.outerHTML);
                        element.remove();
                    }
                }
                this.isDisabledArea = false;
                this.isDraggingExistingCommand = false;
                editor.undo.saveStep();
                ev.preventDefault();
                ev.stopPropagation();

                this.userHasInteracted = true;
                if (this.editorAboveBLFocused()) {
                    this.ensureCursorVisibility(this.editorAboveBL());
                    this.onContentChange(this.editorAboveBL().html.get(), EditorPostion.ABOVE);
                } else {
                    this.ensureCursorVisibility(this.editorBelowBL());
                    this.onContentChange(this.editorBelowBL().html.get(), EditorPostion.BELOW);
                }

                return false;
            },
            true
        );
        editor.events.on(
            'mouseup',
            () => {
                const selectedText = editor.selection.text().trim();
                if (selectedText) {
                    let prodCommand = false;
                    const selTextTest = window
                        .getSelection()
                        .toString()
                        .split('\n')
                        .reduce((acc, el) => {
                            if (el === '[PROD]') {
                                prodCommand = true;
                                return acc;
                            }
                            if (el === '[/PROD]') {
                                prodCommand = false;
                                return acc;
                            }
                            if (!prodCommand) {
                                acc.push(el);
                            }
                            return acc;
                        }, [])
                        .join('\n');
                    this.ngZone.run(() => {
                        this.selectedTextSubject.next(selTextTest);
                        this.clearPreviousSelection = true;
                    });
                }
            },
            true
        );
        editor.events.on(
            'mousedown',
            () => {
                if (this.clearPreviousSelection) {
                    editor.selection.clear();
                    this.ngZone.run(() => {
                        this.selectedTextSubject.next(editor.selection.text().trim());
                        this.clearPreviousSelection = false;
                    });
                }
            },
            true
        );
        editor.events.on(
            'productionCommand',
            (title: ProductionCommandType) => {
                this.ngZone.run(() => {
                    this.productionCommandInsertHandler(title.toUpperCase() as ProductionCommandType);
                });
            },
            true
        );
        // EDITOR.COMMENTS: Left for reference in the future, when we start implementing the comments
        //     editor.events.on(
        //         EditorActions.COMMENT,
        //         () => {
        //             const selectedText = this.editor().selection.text();

        //             if (selectedText) {
        //                 const comment = prompt('Enter your comment:');
        //                 if (comment) {
        //                     const commentId = `comment-${Date.now()}`;
        //                     editor.html.insert(`<span class="comment-highlight" data-comment="${commentId}">${selectedText}</span>`);

        //                     // Store comment data
        //                     this.comments[commentId] = comment;
        //                     /**
        //                      *

        // .comment-highlight {
        //     background-color: yellow;
        //     cursor: pointer;
        // }
        // .comment-highlight:hover {
        //     background-color: orange;
        // }
        //                      */
        //                 }
        //             }
        //         },
        //         true
        //     );
        editor.events.on(
            EditorActions.SAVE,
            () => {
                this.saveTheCurrentStory();
            },
            true
        );
        editor.events.on(
            'keydown',
            (e: KeyboardEvent) => {
                if (e.ctrlKey && !e.shiftKey && !e.altKey) {
                    switch (e.keyCode) {
                        case KeyCodes.S:
                            e.preventDefault();
                            this.saveTheCurrentStory();
                            return false; // Avoid Froala's default shortcut processing
                        case KeyCodes.SEMICOLON:
                            e.preventDefault();
                            this.userHasInteracted = true;
                            this.insertCurrentDateTime();
                            return false;
                        case KeyCodes.D:
                            e.preventDefault();
                            this.userHasInteracted = true;
                            this.ngZone.run(() => {
                                this.productionCommandInsertHandler(ProductionCommandType.DURATION);
                            });
                            return false;
                        case KeyCodes.H:
                            e.preventDefault();
                            this.userHasInteracted = true;
                            this.ngZone.run(() => {
                                this.productionCommandInsertHandler(ProductionCommandType.NOTES);
                            });
                            return false;
                        case KeyCodes.J:
                            e.preventDefault();
                            this.userHasInteracted = true;
                            this.ngZone.run(() => {
                                this.productionCommandInsertHandler(ProductionCommandType.INSERT);
                            });
                            return false;
                        case KeyCodes.K:
                            e.preventDefault();
                            this.userHasInteracted = true;
                            this.ngZone.run(() => {
                                this.productionCommandInsertHandler(ProductionCommandType.TAKE);
                            });
                            return false;
                        case KeyCodes.L:
                            e.preventDefault();
                            this.userHasInteracted = true;
                            this.ngZone.run(() => {
                                this.productionCommandInsertHandler(ProductionCommandType.ANCHOR);
                            });
                            return false;
                        case KeyCodes.V: // Paste
                        case KeyCodes.X: // Cut
                        case KeyCodes.B: // Bold
                        case KeyCodes.I: // Italic
                        case KeyCodes.Z: // Undo
                        case KeyCodes.Y: // Redo
                            this.userHasInteracted = true;
                            break;
                    }
                } else if (e.ctrlKey && e.shiftKey && !e.altKey) {
                    switch (e.keyCode) {
                        case KeyCodes.SEMICOLON:
                            e.preventDefault();
                            this.userHasInteracted = true;
                            this.insertCurrentTime();
                            return false;
                        case KeyCodes.U:
                            e.preventDefault();
                            this.userHasInteracted = true;
                            this.currentEditor().commands.exec('underline');
                            return false;
                    }
                } else if (!e.ctrlKey && !e.metaKey) {
                    // Check for content-modifying keys
                    if (e.keyCode === KeyCodes.ENTER || e.keyCode === KeyCodes.NUMPAD_ENTER) {
                        e.preventDefault();
                        this.userHasInteracted = true;
                    } else if (e.key.length === 1 || e.keyCode === KeyCodes.BACKSPACE || e.keyCode === KeyCodes.DEL) {
                        this.userHasInteracted = true;
                    }
                }
            },
            true
        );
        editor.events.on(
            'cut',
            event => {
                // Prevent the default cut behavior, because when we use the cut command, and we have selected production command or mir object we want to prevent the cut.
                // in that particular case this event is fired, not our custom. but when we have selected only text than our custom event is fired. So we will prevent this,
                // this way we asure that no cut will be performed when we have selected production command or mir object.

                const selectionText = this.currentEditor()?.selection?.text();
                this.userHasInteracted = true;
                if (!selectionText || selectionText.includes('[PROD]') || selectionText.includes('[MOS]') || selectionText.includes('[/PROD]') || selectionText.includes('[/MOS]')) {
                    this.userHasInteracted = false;
                    event.preventDefault();
                    event.stopPropagation();
                    return false;
                }
            },
            true
        );
    }

    onArrowUpClick() {
        const aboveBlackLineEl = this.aboveBlackLine().nativeElement;
        const belowBlackLineEl = this.belowBlackLine().nativeElement;
        if (this.currentPosition() === BlackLinePosition.BOTTOM) {
            aboveBlackLineEl.classList.remove(BlackLinePosition.BOTTOM);
            belowBlackLineEl.classList.remove(BlackLinePosition.BOTTOM);
            aboveBlackLineEl.classList.add(BlackLinePosition.MIDDLE);
            this.currentPosition.set(BlackLinePosition.MIDDLE);
        } else if (this.currentPosition() === BlackLinePosition.MIDDLE) {
            aboveBlackLineEl.classList.remove(BlackLinePosition.MIDDLE);
            aboveBlackLineEl.classList.add(BlackLinePosition.TOP);
            this.currentPosition.set(BlackLinePosition.TOP);
        }
    }

    onArrowDownClick() {
        const aboveBlackLineEl = this.aboveBlackLine().nativeElement;
        const belowBlackLineEl = this.belowBlackLine().nativeElement;
        if (this.currentPosition() === BlackLinePosition.TOP) {
            aboveBlackLineEl.classList.remove(BlackLinePosition.TOP);
            aboveBlackLineEl.classList.add(BlackLinePosition.MIDDLE);
            this.currentPosition.set(BlackLinePosition.MIDDLE);
        } else if (this.currentPosition() === BlackLinePosition.MIDDLE) {
            aboveBlackLineEl.classList.remove(BlackLinePosition.MIDDLE);
            aboveBlackLineEl.classList.add(BlackLinePosition.BOTTOM);
            belowBlackLineEl.classList.add(BlackLinePosition.BOTTOM);
            this.currentPosition.set(BlackLinePosition.BOTTOM);
        }
    }

    private handleOtherCommandsOnInsert(event: ProductionCommandCreateEvent) {
        (this.currentCommandDialogRef.componentInstance.onInsert as unknown as Observable<ProductionCommand>)
            .pipe(
                tap((command: ProductionCommand) => {
                    if (!this.story() || !command) {
                        return;
                    }
                    const textProdCommand = this.convertProductionCommandToText(command as ProductionCommand);
                    if (!textProdCommand) {
                        return;
                    }
                    const newElId = v4();
                    const textCommands = this.story().TextCommands ? { ...this.story().TextCommands } : {};
                    textCommands[newElId] = textProdCommand.startsWith('[') && textProdCommand.endsWith(']') ? textProdCommand : `[${textProdCommand}]`;
                    this.story().TextCommands = textCommands;
                    const newStory = { ...this.story() };
                    let parsedStoryText = parseXml(newStory.text, 'text/html');

                    const productionItemElement = ML2HtmlUtils.generateProductionCommandHtml(textProdCommand.replace(/\r\n|\n/g, '<br/>'), newElId);
                    if (productionItemElement) {
                        // handle it with the pointer and the html from the editor, not from the story record
                        const content = this.currentEditor().html.get();

                        event.pointerProductionCommand = content.substring(0, event.pointerProductionCommand).endsWith('[/PROD]')
                            ? event.pointerProductionCommand + 12
                            : event.pointerProductionCommand;
                        const partBeforePointer = content.substring(0, event.pointerProductionCommand);
                        const parentTag = findParentTag(partBeforePointer);
                        let updatedContent = '';
                        const contentAfterPointer = content.substring(event.pointerProductionCommand);
                        if (parentTag) {
                            updatedContent = `${partBeforePointer ? partBeforePointer : ''}</${parentTag}>${productionItemElement.outerHTML}<${parentTag}>${contentAfterPointer}`;
                        } else {
                            updatedContent = `${partBeforePointer ? partBeforePointer : ''}${productionItemElement.outerHTML}${contentAfterPointer}`;
                        }
                        parsedStoryText = parseXml(updatedContent, 'text/html');

                        if (event.editorPosition === EditorPostion.BELOW) {
                            newStory.text = `${this.contentAboveBL()}${blackLineSpan}${parsedStoryText.body.innerHTML}`;
                            if (this.codoxPluginBelowBLAvailable) {
                                this.codoxPluginBelowBL?.forceSync(parsedStoryText.body.innerHTML);
                            } else {
                                this.contentBelowBL.set(parsedStoryText.body.innerHTML);
                            }
                        } else {
                            newStory.text = `${parsedStoryText.body.innerHTML}${blackLineSpan}${this.contentBelowBL()}`;
                            if (this.codoxPluginAboveBLAvailable) {
                                this.codoxPluginAboveBL?.forceSync(parsedStoryText.body.innerHTML);
                            } else {
                                this.contentAboveBL.set(parsedStoryText.body.innerHTML);
                            }
                        }

                        if (newElId) {
                            this.pendingCursorPositionCmdId = newElId;
                        }

                        this.handleContentChanged(newStory);
                    } else {
                        console.warn('Failed to parse production command item');
                    }
                })
            )
            .subscribe();
    }

    private handleAnchorReadRateCommandOnInsert(event: ProductionCommandCreateEvent) {
        (this.currentCommandDialogRef.componentInstance.onInsert as unknown as Observable<ProductionCommand[]>)
            .pipe(
                tap((commands: ProductionCommand[]) => {
                    if (!this.story() || !commands) {
                        return;
                    }
                    const textCommands = this.story().TextCommands ? { ...this.story().TextCommands } : {};
                    const newStory = { ...this.story() };
                    // handle it with the pointer and the html from the editor, not from the story record
                    let content = this.currentEditor().html.get();

                    let lastPointerPosition = event.pointerProductionCommand;
                    let lastCmdId = '';
                    for (const command of commands) {
                        if (!command || !command[ProductionFieldName.TEXT]) {
                            continue;
                        }

                        const textProdCommand = this.convertProductionCommandToText(command as ProductionCommand);
                        if (!textProdCommand) {
                            continue;
                        }

                        const newElId = v4();
                        lastCmdId = newElId;
                        textCommands[newElId] = textProdCommand.startsWith('[') && textProdCommand.endsWith(']') ? textProdCommand : `[${textProdCommand}]`;

                        const productionItemElement = ML2HtmlUtils.generateProductionCommandHtml(textProdCommand.replace(/\r\n|\n/g, '<br/>'), newElId);
                        if (productionItemElement) {
                            const beforeContent = content.substring(0, lastPointerPosition);
                            const afterContent = content.substring(lastPointerPosition);
                            content = beforeContent + productionItemElement.outerHTML + afterContent;
                            lastPointerPosition = beforeContent.length + productionItemElement.outerHTML.length;
                        }
                    }

                    if (textCommands && Object.keys(textCommands).length > 0) {
                        this.story().TextCommands = textCommands;
                        const parsedStoryText = parseXml(content, 'text/html');
                        if (event.editorPosition === EditorPostion.BELOW) {
                            newStory.text = `${this.contentAboveBL()}${blackLineSpan}${parsedStoryText.body.innerHTML}`;
                            if (this.codoxPluginBelowBLAvailable) {
                                this.codoxPluginBelowBL?.forceSync(parsedStoryText.body.innerHTML);
                            } else {
                                this.contentBelowBL.set(parsedStoryText.body.innerHTML);
                            }
                        } else {
                            newStory.text = `${parsedStoryText.body.innerHTML}${blackLineSpan}${this.contentBelowBL()}`;
                            if (this.codoxPluginAboveBLAvailable) {
                                this.codoxPluginAboveBL?.forceSync(parsedStoryText.body.innerHTML);
                            } else {
                                this.contentAboveBL.set(parsedStoryText.body.innerHTML);
                            }
                        }
                        if (lastCmdId) {
                            this.pendingCursorPositionCmdId = lastCmdId;
                        }
                        this.handleContentChanged(newStory);
                    }
                })
            )
            .subscribe();
    }

    private handleOtherCommandsOnUpdate(updateCommand: ProductionCommand, newStory: StorytellingStoryRecord) {
        const parsedStoryText = parseXml(newStory.text, 'text/html');
        const textCommands = { ...(this.story().TextCommands || {}) };
        // Handle single command
        const cmd = updateCommand;
        if ((cmd?.commandType === ProductionCommandType.DURATION && !cmd[cmd.commandType]) || (!cmd[ProductionFieldName.TEXT] && cmd.commandType === ProductionCommandType.NOTES)) {
            // Remove empty command
            this.removeCommandObject([cmd.guid]);
        } else {
            const textProdCommand = this.convertProductionCommandToText(cmd);
            if (textProdCommand) {
                textCommands[cmd.guid] = textProdCommand.startsWith('[') && textProdCommand.endsWith(']') ? textProdCommand : `[${textProdCommand}]`;
                const productionItemElement = ML2HtmlUtils.generateProductionCommandHtml(textProdCommand.replace(/\r\n|\n/g, '<br/>'), cmd.guid);

                if (productionItemElement) {
                    const productionItem = parsedStoryText.getElementById(cmd.guid);
                    if (productionItem) {
                        productionItem.replaceWith(productionItemElement);
                    }
                    newStory.text = parsedStoryText.documentElement.outerHTML;
                    const contents = newStory.text?.split(blackLineRegex);
                    const [aboveBlackLineContent, , belowBlackLineContent] = contents || [];
                    if (this.codoxPluginAboveBLAvailable) {
                        this.codoxPluginAboveBL?.forceSync(aboveBlackLineContent || '');
                    } else {
                        this.contentAboveBL.set(aboveBlackLineContent || '');
                    }
                    if (this.codoxPluginBelowBLAvailable) {
                        this.codoxPluginBelowBL?.forceSync(belowBlackLineContent || '');
                    } else {
                        this.contentBelowBL.set(belowBlackLineContent || '');
                    }
                } else {
                    console.warn('Failed to parse mos item');
                }
            }
        }

        // Update story and emit changes
        newStory.TextCommands = textCommands;
        if (cmd.guid) {
            this.pendingCursorPositionCmdId = cmd.guid;
        }
        this.handleContentChanged(newStory);
    }

    private handleAnchorReadRateCommandOnUpdate([anchorCommand, readRateCommand]: ProductionCommand[], newStory: StorytellingStoryRecord) {
        const textCommands = { ...(this.story().TextCommands || {}) };

        // Handle ANCHOR/READRATE commands
        let content = this.currentEditor().html.get();

        const hostElement = this.elementRef.nativeElement as HTMLElement;

        const prodCommands = hostElement.querySelectorAll('.st-rich-text-editor--production');

        let anchorCommandElement = null;
        let readRateCommandElement = null;
        if (prodCommands.length > 0) {
            prodCommands.forEach(el => {
                if (el.id === anchorCommand?.guid) {
                    anchorCommandElement = el;
                }
                if (el.id === readRateCommand?.guid) {
                    readRateCommandElement = el;
                }
            });
        }

        // TODO: can't believe it doesn't work, leave it here for now, to investigate in the future
        // const anchorElId = anchorCommand?.guid ? `#${anchorCommand.guid}` : null;
        // const readRateElId = readRateCommand?.guid ? `#${readRateCommand.guid}` : null;
        // const anchorCommandElement = anchorCommand?.guid ? hostElement?.querySelector(anchorElId) : null;
        // const readRateCommandElement = readRateCommand?.guid ? hostElement?.querySelector(readRateElId) : null;
        if (!anchorCommandElement && !readRateCommandElement) {
            console.error('Should never end up here');
            return;
        }

        let lastCmdId = null;

        // First iterate over the commands, and add them to the dom if they don't exist
        for (const cmd of [anchorCommand, readRateCommand]) {
            const existingDomCommand: Element = cmd.commandType === ProductionCommandType.ANCHOR ? anchorCommandElement : readRateCommandElement;
            if (!cmd?.guid) {
                cmd.guid = v4();
            }
            const textProdCommand = this.convertProductionCommandToText(cmd);
            if (!textProdCommand) {
                continue;
            }

            textCommands[cmd.guid] = textProdCommand.startsWith('[') && textProdCommand.endsWith(']') ? textProdCommand : `[${textProdCommand}]`;
            const productionItemElement = ML2HtmlUtils.generateProductionCommandHtml(textProdCommand.replace(/\r\n|\n/g, '<br/>'), cmd.guid);
            lastCmdId = cmd.guid;
            if (productionItemElement) {
                if (existingDomCommand) {
                    existingDomCommand.replaceWith(productionItemElement);
                    if (cmd.commandType === ProductionCommandType.ANCHOR) {
                        anchorCommandElement = productionItemElement;
                    } else if (cmd.commandType === ProductionCommandType.READRATE) {
                        readRateCommandElement = productionItemElement;
                    }
                } else {
                    if (cmd.commandType === ProductionCommandType.ANCHOR) {
                        if (readRateCommandElement) {
                            const parentElement = readRateCommandElement?.parentElement;
                            parentElement.insertBefore(productionItemElement, readRateCommandElement);
                        }
                    } else if (cmd.commandType === ProductionCommandType.READRATE) {
                        if (anchorCommandElement) {
                            anchorCommandElement.after(productionItemElement);
                        }
                    }
                }
            }
        }

        this.pendingCursorPositionCmdId = lastCmdId;

        if (anchorCommand?.[ProductionFieldName.TEXT].trim() === '') {
            anchorCommandElement?.remove();
            delete textCommands[anchorCommand.guid];
        }

        if (readRateCommand?.[ProductionFieldName.TEXT].trim() === '') {
            readRateCommandElement?.remove();
            delete textCommands[readRateCommand.guid];
        }

        // Update editor content
        content = this.currentEditor().html.get();
        if (this.editorBelowBLFocused()) {
            if (this.codoxPluginBelowBLAvailable) {
                this.codoxPluginBelowBL?.forceSync(content);
            } else {
                this.contentBelowBL.set(content);
            }
            newStory.text = `${this.contentAboveBL()}${blackLineSpan}${content}`;
        } else {
            if (this.codoxPluginAboveBLAvailable) {
                this.codoxPluginAboveBL?.forceSync(content);
            } else {
                this.contentAboveBL.set(content);
            }
            newStory.text = `${content}${blackLineSpan}${this.contentBelowBL()}`;
        }

        // Update story and emit changes
        newStory.TextCommands = textCommands;
        this.handleContentChanged(newStory);
    }

    private handleAnchorReadRateCommandOnEdit(parsedProdCommand: ProductionCommand, commandId: string) {
        // Get the content to check for adjacent ANCHOR/READRATE commands
        const content = this.currentEditor().html.get().replaceAll('<p><br></p>', '').replaceAll('<br>', '').replaceAll('<span>&nbsp;</span>', '').replaceAll('<span></span>', '');
        const parsedStoryText = parseXml(content, 'text/html');
        // Find the current command element
        const currentElement = parsedStoryText.getElementById(commandId);
        if (!currentElement) return;
        // Check for adjacent ANCHOR/READRATE command
        let elementSibling = null;
        let adjacentCommandId = null;
        if (parsedProdCommand.commandType === ProductionCommandType.ANCHOR) {
            elementSibling = currentElement.nextElementSibling;
        } else if (parsedProdCommand.commandType === ProductionCommandType.READRATE) {
            elementSibling = currentElement.previousElementSibling;
        }

        if (elementSibling?.classList.contains('st-rich-text-editor--production')) {
            adjacentCommandId = elementSibling.id;
        }

        // If we found an adjacent command, check if it's the matching type (ANCHOR/READRATE)
        let adjacentCommand: ProductionCommand = null;

        if (adjacentCommandId && this.story()?.TextCommands?.[adjacentCommandId]) {
            let parsedAdjacentCommand: ProductionCommand = null;
            try {
                parsedAdjacentCommand = parseProductionCommand(this.story()?.TextCommands?.[adjacentCommandId], adjacentCommandId);
            } catch (error) {
                if (error instanceof Error && error.message?.startsWith('Unknown command type')) {
                    this.showFailedToParseCommandNotification();
                }
            }
            if (!parsedAdjacentCommand) {
                console.warn('Failed to parse adjacent production command');
                return;
            }

            if (parsedProdCommand.commandType === ProductionCommandType.ANCHOR && parsedAdjacentCommand?.commandType === ProductionCommandType.READRATE) {
                adjacentCommand = parsedAdjacentCommand;
            } else if (parsedProdCommand.commandType === ProductionCommandType.READRATE && parsedAdjacentCommand?.commandType === ProductionCommandType.ANCHOR) {
                adjacentCommand = parsedAdjacentCommand;
            }
        }

        let anchorCommand: ProductionCommand = null;
        let readRateCommand: ProductionCommand = null;
        if (parsedProdCommand.commandType === ProductionCommandType.ANCHOR) {
            anchorCommand = parsedProdCommand;
            readRateCommand = adjacentCommand;
        } else if (parsedProdCommand.commandType === ProductionCommandType.READRATE) {
            readRateCommand = parsedProdCommand;
            anchorCommand = adjacentCommand;
        }

        const commands = [
            {
                commandType: ProductionCommandType.ANCHOR,
                guid: anchorCommand?.[ProductionFieldName.TEXT] ? anchorCommand?.guid || v4() : '',
                [ProductionFieldName.TEXT]: anchorCommand?.[ProductionFieldName.TEXT] || '',
            },
            {
                commandType: ProductionCommandType.READRATE,
                guid: readRateCommand?.[ProductionFieldName.TEXT] ? readRateCommand?.guid || v4() : '',
                [ProductionFieldName.TEXT]: readRateCommand?.[ProductionFieldName.TEXT] || '',
            },
        ];
        this.openEditProductionCommandDialog(commands);
    }

    addMosItemToStoryListener(mosCommand: string) {
        this.confirmApprovedStoryEdit(() => {
            this.handleAddMosItem(mosCommand);
        });
    }

    editMosItemInStoryListener(mosCommand: string, mosElId: string) {
        this.confirmApprovedStoryEdit(() => {
            this.userHasInteracted = true;
            if (!this.story()) {
                console.warn('No story found to edit mos command');
                return;
            }

            const currentStory = { ...this.story() };

            let foundMatchingCommand = false;
            currentStory.TextCommands = Object.entries(currentStory.TextCommands || {}).reduce(
                (acc, [key, value]) => {
                    // mosElId is the element ID (SHA1 of objID), which is also the TextCommands key.
                    if (key === mosElId) {
                        value = mosCommand.startsWith('[') && mosCommand.endsWith(']') ? mosCommand : `[${mosCommand}]`;
                        foundMatchingCommand = true;
                    }
                    acc[key] = value;
                    return acc;
                },
                {} as Record<string, string>
            );

            if (!foundMatchingCommand) {
                console.warn('Failed to find matching mos command to edit, mos command will be added as new command');
                return;
            }

            if (!currentStory.TextCommands) {
                currentStory.TextCommands = {};
            }
            const newStory = { ...currentStory };
            newStory.TextCommands = { ...currentStory.TextCommands };
            let parsedStoryText = parseXml(newStory.text, 'text/html');
            if (!parsedStoryText?.body) {
                parsedStoryText = parseXml('<html xmlns="http://www.w3.org/1999/xhtml"><body><p></p></body></html>', 'text/html');
            }

            const mosItemElement = ML2HtmlUtils.generateMosItemHtml(mosCommand, mosElId);
            if (mosItemElement) {
                const mosItem = parsedStoryText.getElementById(mosElId);
                if (mosItem) {
                    mosItem.replaceWith(mosItemElement);
                }
                newStory.text = parsedStoryText.documentElement.outerHTML;
            } else {
                console.warn('Failed to parse mos item');
            }

            this.emitContentChanged([
                { key: PSTFieldNames.PSTRichText, value: newStory.text },
                { key: PSTFieldNames.TextCommands, value: newStory.TextCommands },
            ]);
        });
    }

    private handleAddMosItem(mosCommand: string) {
        const newElId = v4();
        const newStory = { ...this.story() };
        const isBelowBL = this.lastActiveEditorPosition() === EditorPostion.BELOW;

        const mosItemElement = ML2HtmlUtils.generateMosItemHtml(mosCommand, newElId);
        if (mosItemElement) {
            let textCommands = newStory.TextCommands ? { ...newStory.TextCommands } : {};
            if (!textCommands || Object.keys(textCommands).length === 0) {
                textCommands = {};
            }
            if (newElId) {
                this.pendingCursorPositionCmdId = newElId;
            }
            textCommands[newElId] = mosCommand;
            newStory.TextCommands = textCommands;
            let parsedStoryText = parseXml(newStory.text, 'text/html');
            if (!parsedStoryText?.body) {
                parsedStoryText = parseXml('<html xmlns="http://www.w3.org/1999/xhtml"><body><p></p></body></html>', 'text/html');
            }
            this.currentEditor().selection.restore();
            if (!this.currentEditor().undo.canDo()) this.currentEditor().undo.saveStep();
            let uniqueMarker = '<span id="unique-caret-marker"></span>';
            this.currentEditor().html.insert(uniqueMarker);
            let content = this.currentEditor().html.get();
            const isuniqueMarkerPlacedInSpan = content.includes('<span>' + uniqueMarker + '</span>');
            uniqueMarker = isuniqueMarkerPlacedInSpan ? '<span>' + uniqueMarker + '</span>' : uniqueMarker;
            let caretPosition = content.indexOf(uniqueMarker);
            if (caretPosition < 0) {
                caretPosition = 0;
            }
            content = content.replace(uniqueMarker, '');
            this.currentEditor().html.set(content);
            caretPosition = content.substring(0, caretPosition).endsWith('[/MOS]') ? caretPosition + 12 : caretPosition;
            const partBeforePointer = content.substring(0, caretPosition);
            const parentTag = findParentTag(partBeforePointer);
            let updatedContent = '';
            if (parentTag) {
                updatedContent = `${partBeforePointer}</${parentTag}>${mosItemElement.outerHTML}<${parentTag}>${content.substring(caretPosition)}`;
            } else {
                updatedContent = `${partBeforePointer ? partBeforePointer : ''}${mosItemElement.outerHTML}${content.substring(caretPosition)}`;
            }

            parsedStoryText = parseXml(updatedContent, 'text/html');

            if (isBelowBL) {
                newStory.text = `${this.contentAboveBL()}${blackLineSpan}${parsedStoryText.body.innerHTML}`;
                if (this.codoxPluginBelowBLAvailable) {
                    this.codoxPluginBelowBL?.forceSync(parsedStoryText.body.innerHTML);
                } else {
                    this.contentBelowBL.set(parsedStoryText.body.innerHTML);
                }
            } else {
                newStory.text = `${parsedStoryText.body.innerHTML}${blackLineSpan}${this.contentBelowBL()}`;
                if (this.codoxPluginAboveBLAvailable) {
                    this.codoxPluginAboveBL?.forceSync(parsedStoryText.body.innerHTML);
                } else {
                    this.contentAboveBL.set(parsedStoryText.body.innerHTML);
                }
            }

            this.emitContentChanged([
                {
                    key: PSTFieldNames.PSTRichText,
                    value: newStory.text,
                },
                {
                    key: PSTFieldNames.TextCommands,
                    value: newStory.TextCommands,
                },
            ]);
        } else {
            console.error('Failed to parse mos item');
        }
    }

    private confirmApprovedStoryEdit(onConfirmCallback: () => void) {
        if (this.story()?.Approved) {
            this.isProcessingContentChange = true;
            this.modalService
                .openModal({
                    title: this.languageSettings.PSTWebModalWarningTitle,
                    content: this.languageSettings.PSTWebConfirmEditApprovedStory,
                    yesString: this.languageSettings.PSTWebOkButtonLabel,
                    noString: this.languageSettings.PSTWebCancelButtonLabel,
                })
                .result.pipe(
                    take(1),
                    catchError(() => {
                        return [ModalResponseCodes.CANCEL];
                    }),
                    finalize(() => {
                        this.isProcessingContentChange = false;
                    })
                )
                .subscribe(res => {
                    if (res === ModalResponseCodes.OK) {
                        // Set final PSTApproved value to false, as the story is being edited by user after approval
                        this.emitContentChanged([
                            {
                                key: PSTFieldNames.Approved,
                                value: false,
                            },
                        ]);
                        onConfirmCallback();
                    }
                });
        } else {
            // No confirmation needed, proceed directly
            onConfirmCallback();
        }
    }

    // Shortcuts

    private insertCurrentTime() {
        const editor = this.currentEditor();
        if (editor) {
            if (!editor.undo.canDo()) editor.undo.saveStep();
            editor.html.insert(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS));
            this.onContentChange(editor.html.get(), this.editorBelowBLFocused() ? EditorPostion.BELOW : EditorPostion.ABOVE);

            editor.undo.saveStep();
            editor.selection.save();
            editor.selection.restore();
        }
    }
    private insertCurrentDateTime() {
        const editor = this.currentEditor();
        if (editor) {
            if (!editor.undo.canDo()) editor.undo.saveStep();
            editor.html.insert(DateTime.now().toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS));
            this.onContentChange(editor.html.get(), this.editorBelowBLFocused() ? EditorPostion.BELOW : EditorPostion.ABOVE);

            editor.undo.saveStep();
            editor.selection.save();
            editor.selection.restore();
        }
    }
    private saveTheCurrentStory() {
        this.currentEditor().selection.save();
        this.cursorRestorePending = true;

        if (this.saveEnabled) {
            this.save.emit('');
        }
    }
    // End Shortcuts

    private productionCommandInsertHandler(title: ProductionCommandType) {
        // Insert a unique HTML marker
        // TODO: see if it can be done better by using the editor from the parameter
        this.currentEditor().selection.restore();
        if (!this.currentEditor().undo.canDo()) this.currentEditor().undo.saveStep();
        let uniqueMarker = '<span id="unique-caret-marker"></span>';
        this.currentEditor().html.insert(uniqueMarker);
        let content = this.currentEditor().html.get();
        const isuniqueMarkerPlacedInSpan = content.includes('<span>' + uniqueMarker + '</span>');
        uniqueMarker = isuniqueMarkerPlacedInSpan ? '<span>' + uniqueMarker + '</span>' : uniqueMarker;
        let caretPosition = content.indexOf(uniqueMarker);
        if (caretPosition < 0) {
            caretPosition = 0;
        }
        content = content.replace(uniqueMarker, '');
        this.currentEditor().html.set(content);

        this.onProductionCommandCreate({
            productionCommadType: title,
            pointerProductionCommand: caretPosition,
            guid: this.story().Guid,
            editorPosition: this.lastActiveEditorPosition() === EditorPostion.BELOW ? EditorPostion.BELOW : EditorPostion.ABOVE,
        });
    }

    private restorePendingCursorPosition() {
        if (!this.pendingCursorPositionCmdId) return;

        const element = this.currentEditor()?.el.querySelector(`#${CSS.escape(this.pendingCursorPositionCmdId)}`);

        if (element && this.currentEditor()) {
            const range = document.createRange();
            const selection = window.getSelection();
            range.setStartAfter(element);
            range.setEndAfter(element);
            selection.removeAllRanges();
            selection.addRange(range);
            this.currentEditor().selection.restore();
            this.pendingCursorPositionCmdId = null;
        }
    }

    private showFailedToParseCommandNotification() {
        this.store.dispatch(
            ApplicationActions.showAlertMessage({
                message: this.languageSettings[I18nKeys.ProductionCommandNotFoundMessage],
                title: this.languageSettings[I18nKeys.PSTWebModalWarningTitle],
            })
        );
    }
}

export const HTML_ALLOWED_EMPTY_TAGS = [
    'span.st-rich-text-editor--production-expander',
    'span.st-rich-text-editor--ProductionCommand-Expand-Icon',
    'span.st-rich-text-editor--production-details',
    'span.material-symbols-outlined',
];

export const htmlAllowedAttrs = [
    'mosclass',
    'mosid',
    'accept',
    'accept-charset',
    'accesskey',
    'action',
    'align',
    'allowfullscreen',
    'allowtransparency',
    'alt',
    'aria-.*',
    'async',
    'autocomplete',
    'autofocus',
    'autoplay',
    'autosave',
    'background',
    'bgcolor',
    'border',
    'charset',
    'cellpadding',
    'cellspacing',
    'checked',
    'cite',
    'class',
    'color',
    'cols',
    'colspan',
    'content',
    'contenteditable',
    'contextmenu',
    'controls',
    'coords',
    'data',
    'data-.*',
    'datetime',
    'default',
    'defer',
    'dir',
    'dirname',
    'disabled',
    'download',
    'draggable',
    'dropzone',
    'enctype',
    'for',
    'form',
    'formaction',
    'frameborder',
    'headers',
    'height',
    'hidden',
    'high',
    'href',
    'hreflang',
    'http-equiv',
    'icon',
    'id',
    'ismap',
    'itemprop',
    'keytype',
    'kind',
    'label',
    'lang',
    'language',
    'list',
    'loop',
    'low',
    'max',
    'maxlength',
    'media',
    'method',
    'min',
    'mozallowfullscreen',
    'multiple',
    'muted',
    'name',
    'novalidate',
    'open',
    'optimum',
    'pattern',
    'ping',
    'placeholder',
    'playsinline',
    'poster',
    'preload',
    'pubdate',
    'radiogroup',
    'readonly',
    'rel',
    'required',
    'reversed',
    'rows',
    'rowspan',
    'sandbox',
    'scope',
    'scoped',
    'scrolling',
    'seamless',
    'selected',
    'shape',
    'size',
    'sizes',
    'span',
    'src',
    'srcdoc',
    'srclang',
    'srcset',
    'start',
    'step',
    'summary',
    'spellcheck',
    'style',
    'tabindex',
    'target',
    'title',
    'type',
    'translate',
    'usemap',
    'value',
    'valign',
    'webkitallowfullscreen',
    'width',
    'wrap',
    'mat-icon',
    'mat-icon-button',
];
